/**
 * Live button-press list for a single day in the account's timezone.
 * Server-stamped `pressedAt` is the field we filter on. The day
 * boundary is computed in `tz` and converted to UTC `Date`s for the
 * Firestore range query.
 */

import { useCallback, useEffect, useState } from 'react';
import { fromZonedTime } from 'date-fns-tz';

import { buttonPressesInRangeQuery, subscribe } from '@/lib/firestore';
import { log } from '@/lib/logger';
import type { ButtonPress } from '@/schemas';

export type ButtonPressWithId = ButtonPress & { id: string };

export function useButtonPresses(
  aid: string | null,
  date: string,
  tz: string,
): {
  presses: ButtonPressWithId[];
  loading: boolean;
  /** True when the listener has given up (see subscribe's onError) —
   * `presses` may be stale or empty. `retry` re-attaches. */
  error: boolean;
  retry: () => void;
} {
  // Derived loading: see useMyMemberships for the why. Multi-arg key
  // combined into one comparable string.
  const key = aid && date && tz ? `${aid}|${date}|${tz}` : null;
  const [state, setState] = useState<{ loadedKey: string | null; data: ButtonPressWithId[] }>({
    loadedKey: null,
    data: [],
  });
  const [error, setError] = useState(false);
  const [retryNonce, setRetryNonce] = useState(0);
  const retry = useCallback(() => {
    setError(false);
    setRetryNonce((n) => n + 1);
  }, []);

  useEffect(() => {
    if (!aid || !date || !tz) {
      setState({ loadedKey: null, data: [] });
      return;
    }
    setState({ loadedKey: null, data: [] });
    // [start, end) in the account timezone, expressed as UTC Date
    // boundaries for the Firestore range filter.
    const start = fromZonedTime(`${date}T00:00:00`, tz);
    const end = fromZonedTime(`${nextDay(date)}T00:00:00`, tz);
    const unsub = subscribe<ButtonPress>(
      buttonPressesInRangeQuery(aid, start, end),
      (rows) => {
        setError(false);
        setState({ loadedKey: `${aid}|${date}|${tz}`, data: rows });
      },
      (err) => {
        log.error('button-presses listener failed', { aid, error: String(err) });
        setError(true);
      },
    );
    return unsub;
  }, [aid, date, tz, retryNonce]);

  return {
    presses: state.loadedKey === key ? state.data : [],
    loading: key != null && state.loadedKey !== key && !error,
    error,
    retry,
  };
}

function nextDay(yyyyMmDd: string): string {
  const dt = new Date(`${yyyyMmDd}T00:00:00Z`);
  dt.setUTCDate(dt.getUTCDate() + 1);
  return dt.toISOString().slice(0, 10);
}
